import { useState, useEffect, useRef } from "react";
import { useUser } from "src/business-types/grocery/components/UserContext";
import useConfigration from "src/business-types/grocery/hooks/useConfigration";
import { errorHandling } from "src/shared/utils/errorHandling";
import { normalizeFoodTypes } from "src/shared/utils/normalizeFoodTypes";
import { normalizeShiftTypes } from "src/shared/utils/normalizeShiftTypes";
import { notifyWarning, notifySuccess } from "src/assets/toastService";
import { ConfigurationState, PrintType, OrderTableType, BusinessOperationType, BillResetPeriod, DEFAULT_FOOD_TYPES, DEFAULT_SHIFT_MANAGE } from "../types/configuration.types";
import PrintAppFind from "src/print-configuration/PrintAppFind";
import { putPrintConfigPayload } from "../commonPrint/PrintConfiguration";

export const useConfigurationState = () => {
  const { user } = useUser();
  const { config, postConfig, patchConfig } = useConfigration();
  const { newPrinterList, setDefaultPrinters, selectedPrinters: initialSelectedPrinters } = PrintAppFind();

  // Print Settings
  const [printType, setPrintType] = useState<PrintType>("print:1");
  const [orderTableType, setOrderTableType] = useState<OrderTableType>("Table Type:1");
  const [isDualCopy, setIsDualCopy] = useState(false);
  const [printProductLimit, setPrintProductLimit] = useState<string>("5");

  // Table & Billing
  const [tableRows, setTableRows] = useState<number>(10);
  const [billNo, setBillNo] = useState<BillResetPeriod>("noreset");
  const [billNoEnabled, setBillNoEnabled] = useState(false);
  const [deliveryFeeEnabled, setDeliveryFeeEnabled] = useState(false);
  const [cardEnabled, setCardEnabled] = useState(false);
  const [parcelFeeEnabled, setParcelFeeEnabled] = useState(false);
  const [tipsEnabled, setTipsEnabled] = useState(false);
  const [orderIdEnabled, setOrderIdEnabled] = useState(true);
  const [businessName, setBusinessName] = useState<string>("Grocery");
  const [productEditEnabled, setProductEditEnabled] = useState(false);
  const [razorpayEnabled, setRazorpayEnabled] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState<string>("")
  const [razorpayKeySecret, setRazorpayKeySecret] = useState<string>("")


  // Terms & Conditions
  const [addTC, setAddTC] = useState(false);
  const [tcEnglish, setTcEnglish] = useState("");
  const [tcArabic, setTcArabic] = useState("");

  // QR Codes
  const contactInputRef = useRef<HTMLInputElement | null>(null);
  const locationInputRef = useRef<HTMLInputElement | null>(null);
  const [contactQR, setContactQR] = useState<{ file: File | null; preview: string }>({
    file: null,
    preview: "",
  });
  const [locationQR, setLocationQR] = useState<{ file: File | null; preview: string }>({
    file: null,
    preview: "",
  });

  // Reports
  const [pdfProductLimit, setPdfProductLimit] = useState<string>("10");
  const [showOutstanding, setShowOutstanding] = useState<boolean>(true);

  // Food Types
  const [foodTypes, setFoodTypes] = useState<string[]>(DEFAULT_FOOD_TYPES);
  const [newType, setNewType] = useState("");
  const [foodTypesChanged, setFoodTypesChanged] = useState(false);

  // Business Operation
  const [businessOperationEnable, setBusinessOperationEnable] = useState(false);
  const [businessOperationType, setBusinessOperationType] = useState<BusinessOperationType>("Day");
  const [shiftTypes, setShiftTypes] = useState<string[]>(DEFAULT_SHIFT_MANAGE);
  const [newShift, setNewShift] = useState<string>("");

  // Homepage
  const [isProductsNotesEnabled, setIsProductsNotesEnabled] = useState(false);
  const [isDefaultTabsEnabled, setIsDefaultTabsEnabled] = useState(false);

  // Printer
  const [selectedPrinters, setSelectedPrinters] = useState<string[]>([]);
  const [appPrinterEnabled, setAppPrinterEnabled] = useState<boolean>(false);

  const toBoolean = (value: any): boolean => value === true || value === "true";

  // Helper function to validate BillResetPeriod
  const validateBillResetPeriod = (value: any): BillResetPeriod => {
    const validPeriods: BillResetPeriod[] = ["noreset", "day", "week", "month", "year"];
    return validPeriods.includes(value) ? value : "noreset";
  };

  // Helper function to validate OrderTableType
  const validateOrderTableType = (value: any): OrderTableType => {
    const validTypes: OrderTableType[] = ["Table Type:1", "Table Type:2"];
    return validTypes.includes(value) ? value : "Table Type:1";
  };

  // Helper function to validate PrintType
  const validatePrintType = (value: any): PrintType => {
    const validTypes: PrintType[] = ["print:1", "print:2", "print:3"];
    return validTypes.includes(value) ? value : "print:1";
  };

  // Helper function to validate BusinessOperationType
  const validateBusinessOperationType = (value: any): BusinessOperationType => {
    const validTypes: BusinessOperationType[] = ["Day", "Shift"];
    return validTypes.includes(value) ? value : "Day";
  };

  // Load initial printer selections
  useEffect(() => {
    if (initialSelectedPrinters?.length) {
      setSelectedPrinters(initialSelectedPrinters);
    }
  }, [initialSelectedPrinters]);

  // Load app printer enabled state
  useEffect(() => {
    if (config?.appPrinterEnabled !== undefined) {
      setAppPrinterEnabled(toBoolean(config?.appPrinterEnabled));
    }
  }, [config?.appPrinterEnabled]);

  // Load configuration
  useEffect(() => {
    if (!config) return;

    // Print Settings - with validation
    setPrintType(validatePrintType(config?.printType));
    setOrderTableType(validateOrderTableType(config?.orderTableType));
    setIsDualCopy(config?.copyQty > 1);
    setPrintProductLimit(config?.printProductLimit || "5");

    // Table & Billing - with validation
    setTableRows(config?.tableDefaultSize || 10);
    setBillNo(validateBillResetPeriod(config?.billResetPeriod));
    setBillNoEnabled(toBoolean(config?.billNoEnabled));
    setDeliveryFeeEnabled(toBoolean(config?.deliveryFeeEnabled));
    setCardEnabled(toBoolean(config?.cardPayment));
    setParcelFeeEnabled(toBoolean(config?.parcelFeeEnabled));
    setTipsEnabled(toBoolean(config?.tipsEnabled));
    setOrderIdEnabled(toBoolean(config?.orderIdEnabled));
    setProductEditEnabled(toBoolean(config?.productEditEnabled));
    setBusinessName(config.customBusinessName || "Grocery");

    // Terms & Conditions
    setAddTC(toBoolean(config?.enableTermsAndConditions));
    setTcEnglish(config?.termsAndConditionsEnglish || "");
    setTcArabic(config?.termsAndConditionsArabic || "");

    // QR Codes
    setContactQR((prev) => ({ ...prev, preview: config.contactQrCode || "" }));
    setLocationQR((prev) => ({ ...prev, preview: config.locationQrCode || "" }));

    // Reports
    setPdfProductLimit(config?.pdfProductLimit || "10");
    setShowOutstanding(toBoolean(config.showOutstandingInPdf ?? true));

    // Food Types
    try {
      const parsedFoodTypes = typeof config?.foodTypes === "string" ? JSON.parse(config.foodTypes) : config?.foodTypes || [];
      const normalized = normalizeFoodTypes(parsedFoodTypes);
      setFoodTypes([...DEFAULT_FOOD_TYPES, ...normalized.filter((type) => !DEFAULT_FOOD_TYPES.includes(type))]);
      setFoodTypesChanged(false); // Reset the changed state when loading from config
    } catch (e) {
      setFoodTypes(DEFAULT_FOOD_TYPES);
      setFoodTypesChanged(false);
    }

    // Business Operation - with validation
    setBusinessOperationEnable(toBoolean(config?.businessOperationEnable));
    setBusinessOperationType(validateBusinessOperationType(config?.businessOperationType));

    try {
      const parsedShiftTypes = typeof config?.shiftTypes === "string" ? JSON.parse(config.shiftTypes) : config?.shiftTypes || [];
      setShiftTypes([...DEFAULT_SHIFT_MANAGE.filter((def) => !parsedShiftTypes.includes(def)), ...normalizeShiftTypes(parsedShiftTypes)]);
    } catch (e) {
      setShiftTypes(DEFAULT_SHIFT_MANAGE);
    }

    // Homepage
    setIsProductsNotesEnabled(toBoolean(config?.isProductsNotesEnabled));
    setIsDefaultTabsEnabled(toBoolean(config?.isDefaultTabsEnabled));
  }, [config]);

  // Handle print type constraint
  useEffect(() => {
    if (orderTableType === "Table Type:2" && printType === "print:1") {
      setPrintType("print:2");
      notifyWarning("Print Type 1 is not supported in Table Type 2.");
    }
  }, [orderTableType, printType]);

  // Food type handlers
  const addFoodType = () => {
    const trimmedType = newType.trim();

    if (trimmedType && !foodTypes.includes(trimmedType) && foodTypes.length < 5) {
      setFoodTypes((prev) => [...prev, trimmedType]);
      setNewType("");
      setFoodTypesChanged(true);
    }
  };

  const removeFoodType = (type: string) => {
    if (DEFAULT_FOOD_TYPES.includes(type)) return;

    setFoodTypes((prev) => prev.filter((t) => t !== type));
    setFoodTypesChanged(true);
  };

  // Shift handlers
  const addShift = () => {
    const trimmedShift = newShift.trim();
    if (trimmedShift && !shiftTypes.includes(trimmedShift) && shiftTypes.length < 5) {
      setShiftTypes([...shiftTypes, trimmedShift]);
      setNewShift("");
    }
  };

  const removeShift = (shift: string) => {
    setShiftTypes(shiftTypes.filter((s) => s !== shift));
  };

  // QR handlers
  const handleSimpleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, setter: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notifyWarning("Only image files allowed");
      return;
    }

    const preview = URL.createObjectURL(file);
    setter({ file, preview });
  };
  const isBlobUrl = (url: string) => url.startsWith("blob:");

  const removeContactQR = () => {
    if (contactQR.preview && isBlobUrl(contactQR.preview)) {
      URL.revokeObjectURL(contactQR.preview);
    }

    setContactQR({ file: null, preview: "" });

    if (contactInputRef.current) {
      contactInputRef.current.value = "";
    }
  };

  const removeLocationQR = () => {
    if (locationQR.preview && isBlobUrl(locationQR.preview)) {
      URL.revokeObjectURL(locationQR.preview);
    }

    setLocationQR({ file: null, preview: "" });

    if (locationInputRef.current) {
      locationInputRef.current.value = "";
    }
  };

  // Printer handlers
  const handlePrinterCheckboxChange = (printerName: string) => {
    setSelectedPrinters((prev) => (prev.includes(printerName) ? prev.filter((p) => p !== printerName) : [...prev, printerName]));
  };

  const setDefaultPrintersHandler = () => {
    setDefaultPrinters(selectedPrinters);
  };

  const saveCustomPrint = async (data: putPrintConfigPayload) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("customEnabiled", data.customEnabiled ? "true" : "false");
      formData.append("layoutId", data.printType || printType);

      await postConfig(formData);
      setFoodTypesChanged(false);
      notifySuccess("Print settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  // Save functions for each tab
  const savePrintSettings = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("printType", data.printType || printType);
      formData.append("orderTableType", data.orderTableType || orderTableType);
      formData.append("copyQty", String(data.isDualCopy !== undefined ? (data.isDualCopy ? 2 : 1) : isDualCopy ? 2 : 1));
      formData.append("printProductLimit", data.printProductLimit || printProductLimit);

      await postConfig(formData);
      notifySuccess("Print settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveTableBilling = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("tableDefaultSize", String(data.tableRows || tableRows));
      formData.append("billResetPeriod", data.billNoEnabled ? data.billNo || billNo : "noreset");
      formData.append("billNoEnabled", String(data.billNoEnabled !== undefined ? data.billNoEnabled : billNoEnabled));
      formData.append("parcelFeeEnabled", String(data.parcelFeeEnabled !== undefined ? data.parcelFeeEnabled : parcelFeeEnabled));
      formData.append("tipsEnabled", String(data.tipsEnabled !== undefined ? data.tipsEnabled : tipsEnabled));
      formData.append("orderIdEnabled", String(data.orderIdEnabled !== undefined ? data.orderIdEnabled : orderIdEnabled));
      formData.append("customBusinessName", data.businessName || businessName);
      formData.append("productEditEnabled", String(data.productEditEnabled !== undefined ? data.productEditEnabled : productEditEnabled));

      await postConfig(formData);
      notifySuccess("Table & Billing settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveTermsConditions = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("enableTermsAndConditions", String(data.addTC !== undefined ? data.addTC : addTC));
      formData.append("termsAndConditionsEnglish", data.tcEnglish || tcEnglish);
      formData.append("termsAndConditionsArabic", data.tcArabic || tcArabic);

      await postConfig(formData);
      notifySuccess("Terms & Conditions saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveQRCodes = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));

      const contactFile = data.contactQR?.file || contactQR.file;
      const locationFile = data.locationQR?.file || locationQR.file;

      if (contactFile) {
        formData.append("contactQrCode", contactFile);
      } else if (!data.contactQR?.preview && !contactQR.preview) {
        formData.append("contactQrCode", "");
      }

      if (locationFile) {
        formData.append("locationQrCode", locationFile);
      } else if (!data.locationQR?.preview && !locationQR.preview) {
        formData.append("locationQrCode", "");
      }

      await postConfig(formData);
      notifySuccess("QR Codes saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveReports = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("pdfProductLimit", data.pdfProductLimit || pdfProductLimit);
      formData.append("showOutstandingInPdf", String(data.showOutstanding !== undefined ? data.showOutstanding : showOutstanding));

      await postConfig(formData);
      notifySuccess("Report settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveFoodTypes = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("foodTypes", JSON.stringify(data.foodTypes || foodTypes));

      await postConfig(formData);
      notifySuccess("Food types saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveBusinessOperation = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("businessOperationEnable", String(data.businessOperationEnable !== undefined ? data.businessOperationEnable : businessOperationEnable));
      formData.append("businessOperationType", data.businessOperationType || businessOperationType);
      formData.append("shiftTypes", JSON.stringify(data.shiftTypes || shiftTypes));
      formData.append("printProductLimit", printProductLimit);

      await postConfig(formData);
      notifySuccess("Business operation settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const saveHomepage = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("isDefaultTabsEnabled", String(data.isDefaultTabsEnabled !== undefined ? data.isDefaultTabsEnabled : isDefaultTabsEnabled));
      formData.append("isProductsNotesEnabled", String(data.isProductsNotesEnabled !== undefined ? data.isProductsNotesEnabled : isProductsNotesEnabled));

      await postConfig(formData);
      notifySuccess("Homepage settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const savePrinterSettings = async () => {
    try {
      setDefaultPrintersHandler();
      if (!toBoolean(config?.appPrinterEnabled)) {
        await patchConfig({ appPrinterEnabled: true });
      }
      notifySuccess("Printer settings saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const disableAppPrinter = async () => {
    try {
      await patchConfig({ appPrinterEnabled: false });
      setAppPrinterEnabled(false);
      notifySuccess("App printer disabled successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  const savePosConfig = async (data: Partial<ConfigurationState>) => {
    try {
      const formData = new FormData();
      // formData.append("orgId", String(user?.orgId));
      formData.append("deliveryFeeEnabled", String(data.deliveryFeeEnabled !== undefined ? data.deliveryFeeEnabled : deliveryFeeEnabled));
      // formData.append("cardPayment", String(data.cardEnabled !== undefined ? data.cardEnabled : cardEnabled));

      await postConfig(formData);
      notifySuccess("POS configuration saved successfully");
    } catch (error) {
      errorHandling(error);
    }
  };

  return {
    user,
    config,
    newPrinterList,
    // Print Settings
    printType,
    setPrintType,
    orderTableType,
    setOrderTableType,
    isDualCopy,
    setIsDualCopy,
    printProductLimit,
    setPrintProductLimit,

    // Table & Billing
    tableRows,
    setTableRows,
    billNo,
    setBillNo,
    billNoEnabled,
    setBillNoEnabled,
    deliveryFeeEnabled,
    setDeliveryFeeEnabled,
    businessName,
    setBusinessName,
    orderIdEnabled,
    setOrderIdEnabled,
    parcelFeeEnabled,
    setParcelFeeEnabled,
    tipsEnabled,
    setTipsEnabled,
    productEditEnabled,
    setProductEditEnabled,

    // Terms & Conditions
    addTC,
    setAddTC,
    tcEnglish,
    setTcEnglish,
    tcArabic,
    setTcArabic,

    // QR Codes
    contactInputRef,
    locationInputRef,
    contactQR,
    setContactQR,
    locationQR,
    setLocationQR,
    handleSimpleImageUpload,
    removeContactQR,
    removeLocationQR,

    // Reports
    pdfProductLimit,
    setPdfProductLimit,
    showOutstanding,
    setShowOutstanding,

    // Food Types
    foodTypes,
    newType,
    setNewType,
    addFoodType,
    removeFoodType,
    foodTypesChanged,
    setFoodTypesChanged,

    // Business Operation
    businessOperationEnable,
    setBusinessOperationEnable,
    businessOperationType,
    setBusinessOperationType,
    shiftTypes,
    newShift,
    setNewShift,
    addShift,
    removeShift,

    // Homepage
    isProductsNotesEnabled,
    setIsProductsNotesEnabled,
    isDefaultTabsEnabled,
    setIsDefaultTabsEnabled,

    // Printer
    selectedPrinters,
    setSelectedPrinters,
    appPrinterEnabled,
    setAppPrinterEnabled,
    handlePrinterCheckboxChange,

    // Save functions
    savePrintSettings,
    saveTableBilling,
    saveTermsConditions,
    saveQRCodes,
    saveReports,
    saveFoodTypes,
    saveBusinessOperation,
    saveHomepage,
    savePrinterSettings,
    disableAppPrinter,

    // Utils
    toBoolean,

    //new Custom Print
    saveCustomPrint,

    //set pos config
    cardEnabled,
    setCardEnabled,
    savePosConfig,
  };
};
